/**
 * Drives the funnel-orchestrator-side analytics events from §8.1 — the
 * subset that can be inferred purely from engine state transitions
 * (`funnel_step_viewed`, `funnel_started`, plus the type-specific
 * `funnel_loading_viewed` / `funnel_character_revealed` /
 * `funnel_first_message_previewed` / `paywall_viewed`).
 *
 * Pairs with `useFunnelAnalytics` (the emitter bag) and is wired in
 * `<Funnel>`. Imperative answer/back/completion events stay in the
 * matching handlers — see `useFunnelHandlers`.
 *
 * Out of scope here:
 *   - `funnel_landing_viewed`           — fired once on initial mount.
 *   - `funnel_email_submitted` /
 *     `funnel_signup_completed`         — wired with the action-tagged
 *                                         `account_create` orchestration.
 *   - `plan_selected` /
 *     `purchase_completed`              — emitted by the brand app.
 *   - `funnel_abandoned`                — server-side inactivity inference.
 */
import { useEffect, useRef } from "react";
import type { Step } from "@nextday/funnel-engine";

import type { FunnelAnalyticsApi } from "@src/funnel/hooks/useFunnelAnalytics";

const STEP_TYPE_TO_EMITTER: ReadonlyMap<
  string,
  (analytics: FunnelAnalyticsApi, step: Step) => void
> = new Map([
  ["loading_screen", (analytics, step) => analytics.funnel_loading_viewed({ stepId: step.id })],
  [
    "character_reveal_gate",
    (analytics, step) => analytics.funnel_character_revealed({ stepId: step.id }),
  ],
  [
    "first_message_preview",
    (analytics, step) => analytics.funnel_first_message_previewed({ stepId: step.id }),
  ],
  ["paywall", (analytics, step) => analytics.paywall_viewed({ stepId: step.id })],
  ["paywall_handoff", (analytics, step) => analytics.paywall_viewed({ stepId: step.id })],
]);

export type UseFunnelAnalyticsLifecycleArgs = {
  analytics: FunnelAnalyticsApi;
  currentStep: Step | null;
  /** Index of the step in the visible-steps list (drives `step_index`). */
  stepIndex: number;
  /** When `true`, the funnel was restored from a draft (e.g. post-auth
   *  redirect). Suppresses `funnel_landing_viewed` and `funnel_started`
   *  to avoid duplicate events. */
  isResumedSession?: boolean;
};

/**
 * Side-effect hook: emits the lifecycle event(s) tied to the current step
 * each time the engine moves. Re-runs only when `currentStep.id` changes
 * (object identity is unstable; the id is the only stable cue).
 */
export const useFunnelAnalyticsLifecycle = ({
  analytics,
  currentStep,
  stepIndex,
  isResumedSession,
}: UseFunnelAnalyticsLifecycleArgs): void => {
  // When resuming from a saved draft (e.g. post-auth redirect), mark
  // landing and started as already-fired so we don't duplicate them.
  const hasStartedRef = useRef(!!isResumedSession);
  const hasLandedRef = useRef(!!isResumedSession);
  const lastStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasLandedRef.current) {
      hasLandedRef.current = true;
      analytics.funnel_landing_viewed();
    }
  }, [analytics]);

  useEffect(() => {
    if (!currentStep) return;
    if (lastStepIdRef.current === currentStep.id) return;
    lastStepIdRef.current = currentStep.id;

    if (!hasStartedRef.current && stepIndex > 1) {
      hasStartedRef.current = true;
      analytics.funnel_started();
    }

    analytics.funnel_step_viewed({ stepId: currentStep.id, stepIndex });

    const typeEmitter = STEP_TYPE_TO_EMITTER.get(currentStep.type);
    if (typeEmitter) typeEmitter(analytics, currentStep);
  }, [analytics, currentStep, stepIndex]);
};

export default useFunnelAnalyticsLifecycle;
